import React from "react";
import {
  GiCompass,
  GiBinoculars,
  GiSewingMachine,
  GiRolledCloth,
  GiClothes,
  GiGemPendant,
  GiCheckMark,
  GiShield,
  GiUpgrade,
} from "react-icons/gi";

import TextileBackground from "../../components/TextileBackground";
import CinematicSection from "../../components/CinematicSection";
import CustomNavbar from "../../components/Navbar";
import Reveal from "../../components/Reveal";
import FooterLinks from "../../components/FooterLinks";
import Footer from "../../components/Footer";
import data from "../../data/Home-page/content";
import catalogue from "../../data/catalogue";

const serviceIcons = [GiSewingMachine, GiRolledCloth, GiClothes];
const valueIcons = [GiGemPendant, GiCheckMark, GiShield, GiUpgrade];

function renderVision(vision) {
  const marker = "women led";
  const idx = vision.toLowerCase().indexOf(marker);
  if (idx === -1) return vision;
  return (
    <>
      {vision.slice(0, idx)}
      <span className="text-gold-700 font-semibold">{vision.slice(idx, idx + marker.length)}</span>
      {vision.slice(idx + marker.length)}
    </>
  );
}

export default function LearnMore() {
  const { paragraphs } = data.about;

  return (
    <TextileBackground>
      <CustomNavbar />

      <CinematicSection zIndex={10} className="bg-cream-100/60" trackHeight="78vh" fitContent>
        <div className="px-6 max-w-3xl mx-auto text-center">
          <Reveal>
            <p className="font-accent italic text-maroon-700 text-base lg:text-lg tracking-[0.3em] uppercase mb-3">
              Our Story
            </p>
          </Reveal>
          <Reveal delay={150}>
            <div className="flex items-center justify-center gap-4">
              <h1 className="font-heading text-maroon-800 text-4xl sm:text-5xl lg:text-6xl font-semibold">
                Learn More About Sutramay
              </h1>
            </div>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-4 space-y-3">
              {paragraphs.map((p, i) => (
                <p key={i} className="font-body text-maroon-900/90 text-sm lg:text-base leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </Reveal>
        </div>
      </CinematicSection>

      <CinematicSection zIndex={15} className="bg-cream-100/60" overflowY="auto" trackHeight="122vh" fitContent>
        <div className="max-w-6xl mx-auto px-6 py-10">
          <Reveal>
            <h2 className="font-heading text-maroon-700 text-2xl sm:text-3xl lg:text-4xl font-medium text-center mb-10">
              Mission &amp; Vision
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <Reveal>
              <article className="bg-cream-50 border-2 border-gold-500/40 rounded-2xl p-6 sm:p-8 h-full">
                <GiCompass className="text-maroon-700 text-3xl mb-4" />
                <h3 className="font-heading text-maroon-700 text-lg lg:text-xl font-medium mb-2">Our Mission</h3>
                <p className="font-body text-maroon-900/90 text-sm lg:text-base leading-relaxed">{data.mission}</p>
              </article>
            </Reveal>
            <Reveal delay={150}>
              <article className="bg-cream-50 border-2 border-gold-500/40 rounded-2xl p-6 sm:p-8 h-full">
                <GiBinoculars className="text-maroon-700 text-3xl mb-4" />
                <h3 className="font-heading text-maroon-700 text-lg lg:text-xl font-medium mb-2">Our Vision</h3>
                <p className="font-body text-maroon-900/90 text-sm lg:text-base leading-relaxed">{renderVision(data.vision)}</p>
              </article>
            </Reveal>
          </div>

          <Reveal>
            <h2 className="font-heading text-maroon-700 text-xl sm:text-2xl lg:text-3xl font-medium text-center mb-8">
              Our Values
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {data.values.map((value, i) => {
              const Icon = valueIcons[i % valueIcons.length];
              return (
                <Reveal key={value.title} delay={i * 120}>
                  <div className="bg-cream-50 border border-gold-500/40 rounded-2xl p-5 text-center h-full">
                    <Icon className="text-maroon-700 text-2xl mx-auto mb-3" />
                    <p className="font-heading text-maroon-700 font-semibold text-xs sm:text-sm lg:text-base">{value.title}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </CinematicSection>

      <CinematicSection zIndex={30} className="bg-cream-100/60" overflowY="auto" trackHeight="86vh" fitContent>
        <div className="max-w-6xl mx-auto px-6 py-10">
          <Reveal>
            <h2 className="font-heading text-maroon-700 text-2xl sm:text-3xl lg:text-4xl font-medium text-center mb-10">
              Our Services
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {data.services.map((service, i) => {
              const Icon = serviceIcons[i % serviceIcons.length];
              return (
                <Reveal key={service.name} delay={i * 150}>
                  <article className="bg-cream-50 border-2 border-gold-500/40 rounded-2xl p-6 h-full">
                    <Icon className="text-maroon-700 text-3xl mb-4" />
                    <h3 className="font-heading text-maroon-700 text-lg lg:text-xl font-medium mb-2">{service.name}</h3>
                    <p className="font-body text-maroon-900/90 text-sm lg:text-base leading-relaxed">{service.description}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </CinematicSection>

      <CinematicSection zIndex={40} className="bg-cream-100/60" overflowY="auto" trackHeight="102vh" fitContent>
        <div className="max-w-6xl mx-auto px-6 py-10">
          <Reveal>
            <p className="font-accent text-gold-700 text-xs lg:text-sm tracking-[0.35em] uppercase text-center mb-3">
              What We Manufacture
            </p>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-heading text-maroon-700 text-2xl sm:text-3xl lg:text-4xl font-medium text-center mb-10">
              Our Products
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <Reveal>
                <h3 className="font-accent text-maroon-900/85 text-xs lg:text-sm tracking-[0.25em] uppercase mb-4 text-center md:text-left">
                  Women's
                </h3>
              </Reveal>
              <div className="grid grid-cols-2 gap-4">
                {catalogue.women.map((item, i) => (
                  <Reveal key={item.name} delay={i * 100}>
                    <div className="bg-cream-50 border border-gold-500/40 rounded-2xl p-5 text-center h-full">
                      <item.Icon className="text-maroon-700 text-2xl mx-auto mb-2" />
                      <p className="font-body text-maroon-900/90 text-xs sm:text-sm lg:text-base">{item.name}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            <div>
              <Reveal>
                <h3 className="font-accent text-maroon-900/85 text-xs lg:text-sm tracking-[0.25em] uppercase mb-4 text-center md:text-left">
                  Men's
                </h3>
              </Reveal>
              <div className="grid grid-cols-2 gap-4">
                {catalogue.men.map((item, i) => (
                  <Reveal key={item.name} delay={i * 100}>
                    <div className="bg-cream-50 border border-gold-500/40 rounded-2xl p-5 text-center h-full">
                      <item.Icon className="text-maroon-700 text-2xl mx-auto mb-2" />
                      <p className="font-body text-maroon-900/90 text-xs sm:text-sm lg:text-base">{item.name}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>

          <Reveal delay={200}>
            <p className="font-body text-maroon-900/85 text-xs lg:text-sm text-center mt-8 max-w-xl mx-auto leading-relaxed">
              These are our manufacturing categories and capabilities — not products available for direct online
              purchase. Contact us to discuss your specific order.
            </p>
          </Reveal>
        </div>
      </CinematicSection>

      <FooterLinks />
      <Footer />
    </TextileBackground>
  );
}
